﻿<template>
  <items-listing-layout class="mailbox-kanban" is-kanban>
    <template #header-left>
      <!-- HEADER TITLE -->
      <listing-header
        :is-loading="isLoadingInitialData"
        :is-custom-filter-submitting="isCustomFilterSubmitting"
        :title="name"
        :entity-id="id"
        :entity-type="EEntities.MAILBOXES"
        :default-filters="defaultFilterNavItems"
        :custom-filters="customFilterNavItems"
        :current-filter="currentFilterNavItem"
        :total-item-count="totalItemCount"
        :active-assignee-counts="activeAssigneeCounts"
        is-kanban
        @save-new-filter="(name) => createCustomFilter(name)"
        @update-custom-filter-name="(name) => updateCustomFilter(name)"
        @delete-custom-filter="(id) => deleteCustomFilter(id)"
        @change-filter="(id) => updateItemTraitSetFromFilterID(id)"
        @select-assignee="(id) => selectAssigneeFilter(id)"
      />
    </template>
    <template #header-right>
      <!-- HEADER OPTIONS -->
      <mailbox-home-header-options
        v-if="$screen.isTabletOrDesktop($mq)"
        :is-loading="isLoadingInitialData"
        :members="members"
        :account-id="accountID"
        :mailbox-id="id"
        :mailbox-name="name"
        :is-direct-connection="isDirectConnection"
        :total-item-count="totalItemCount"
        is-kanban
        @members-added="getMembers"
      />
    </template>
    <template #main-header>
      <!-- MAIN HEADER -->
      <item-filters-panel
        v-model:search-term="itemTraitSet.searchTerm"
        v-model:selected-assignee-ids="itemTraitSet.assigneeIDs"
        v-model:assigned-not-to-you="itemTraitSet.assignedNotToYou"
        v-model:selected-job-appointee-groupings-string="itemTraitSet.jobAppointeeGroupingsString"
        v-model:selected-customer-ids="itemTraitSet.customerIDs"
        v-model:selected-status-ids="itemTraitSet.statusIDs"
        v-model:status-type="itemTraitSet.statusType"
        v-model:selected-priority-ids="itemTraitSet.priorityIDs"
        v-model:selected-due-type="itemTraitSet.dueType"
        v-model:selected-including-tag-ids="itemTraitSet.includingTagIDs"
        v-model:selected-excluding-tag-ids="itemTraitSet.notIncludingTagIDs"
        v-model:created-within-days="itemTraitSet.createdWithinDays"
        v-model:last-updated-within-days="itemTraitSet.lastUpdatedWithinDays"
        v-model:only-watching="itemTraitSet.onlyWatching"
        v-model:is-spam="itemTraitSet.isSpam"
        v-model:start-due-date="itemTraitSet.startDueDate"
        v-model:end-due-date="itemTraitSet.endDueDate"
        v-model:selected-sort="itemTraitSet.sort"
        v-model:custom-field-filters-string="itemTraitSet.customFieldFiltersString"
        :single-mailbox-id="id"
        :is-loading="isLoadingInitialData"
        :is-default-item-trait-set="isDefaultItemTraitSet"
        :assignee-options="members"
        :job-options="jobs"
        :priority-options="priorities"
        :active-status-options="activeStatuses"
        :inactive-status-options="inactiveStatuses"
        :tag-options="tags"
        :due-date-options="dueDateOptions"
        :custom-field-options="customFieldsForFiltering"
        is-kanban
        @reset-filters="resetItemTraitSet"
        @switch-to-list="handleMobileSwitchToList"
        @settings="handleSettings"
      />
    </template>
    <template #main-content>
      <!-- Don't show board if too many conversations -->
      <div v-if="tooManyConversations" class="mailbox-kanban__kanban-disabled-callout">
        <h3>Woops. You have too many cards for this Kanban board!</h3>
        <p>
          We limit Kanban to less than {{maxConversations}} cards. If you have more than that,
          we recommend you delete cards from your
          <router-link
            :to="RouteAssistant.getMailboxRoute(accountID, id, name)"
            class="just-a-link"
          >listing view</router-link>.
        </p>
      </div>
      <template v-else>
        <!-- KANBAN -->
        <kanban-board
          ref="kanbanBoard"
          :is-disabled="isHandlingMove"
          :is-mobile="$screen.isTabletOrMobile($mq)"
          :is-mailbox="true"
          :entity-id="id"
          :tasks="tasksForKanban as ITaskForKanbanWithManualOrder[]"
          :column-orderings="columnOrderings"
          :statuses="statusesForColumns"
          :members="members"
          :customers="customers"
          :priorities="priorities"
          :workflow-status-transitions="workflowStatusTransitions"
          :selected-sort="itemTraitSet.sort"
          :custom-fields="customFields"
          @dragging-started="endPolling"
          @dragging-completed="startPolling"
          @moved-task="(convoID, statusID, idOrder) => handleConvoMove(convoID, statusID, idOrder)"
          @new-task-clicked="sendToNewConversation"
        />
        <!-- MAILBOX INFO -->
        <template v-if="$screen.isDesktop($mq)">
          <entity-info-flyout
            v-if="!isLoadingInitialData"
            v-model:show="showEntityInfoFlyout"
            :entity-id="id"
            :entity-type="EEntities.MAILBOXES"
            :members="members"
            :title="name"
            :workflow="workflow"
          />
        </template>
      </template>
    </template>
    <template #modals>
      <!-- MOBILE NEW BUTTON -->
      <div v-if="$screen.isMobile($mq) && !tooManyConversations" @click="sendToNewConversation">
        <dd-icon-button
          :class="{'go-native-ios' : $screen.isGoNativeiOSMobileApp($mq)}"
          :color="ColorAssistant.MailboxColor"
          icon="plus"
          class="mailbox-kanban__create-button"
          type="circle"
          title="Add conversation"
        />
      </div>
    </template>
  </items-listing-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import dayjs from '@/helpers/dayjs'

/* Mixins */
import EntityLandingMixin from '@/mixins/entity-landing.mixin'

/* Components */
import MailboxHomeHeaderOptions from './components/mailbox-home-header-options.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import TextAssistant from '@/helpers/text-assistant'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'

/* Data types */
import { EEntities, SortOptionValues } from '@/types'
import { IBreadcrumbItemMenuItem, ITaskForKanbanWithManualOrder } from '@/types/data-objects/items'
import { IItemTraitSet } from '@/types/data-objects/listing'
import {
  ICustomField,
  IMailboxesOutputFilterOptionsForListing,
  IKanbanColumnOrdering,
  IStatusForSelection,
  IConvoForKanban,
  ITaskForKanban,
  IWorkflowDetailStatusTransition,
} from '@/api/types/response.types'
import {
  IMailboxesInputManageCustomFilter,
  IMailboxesInputUpdateCustomFilter,
  IConvosInputUpdateForKanban,
} from '@/api/types/request.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import ConversationsAPI from '@/api/modules/convos.api'
import CustomFiltersAPI from '@/api/modules/custom-filters.api'
import CustomersAPI from '@/api/modules/customers.api'

export default defineComponent({
  name: 'MailboxKanban',

  components: {
    ItemsListingLayout,
    MailboxHomeHeaderOptions,
  },

  extends: EntityLandingMixin,

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      maxConversations: 5000,
      initialKanbanDataLoaded: false,
      isHandlingMove: false,
      columnOrderings: [] as Array<IKanbanColumnOrdering>,
      conversations: [] as Array<IConvoForKanban>,
      statusesForColumns: [] as Array<IStatusForSelection>,
      workflowStatusTransitions: [] as Array<IWorkflowDetailStatusTransition>,
      customFields: [] as ICustomField[],
      // For entity-landing mixin
      isMailbox: true,
      isDirectConnection: false,
    }
  },

  computed: {
    noConversations(): boolean {
      return this.initialKanbanDataLoaded && this.conversations.length === 0
    },
    tooManyConversations(): boolean {
      return this.conversations.length > this.maxConversations
    },
    /* Map IConvoForKanban to ITaskForKanban so the kanban-board component can consume them */
    tasksForKanban(): ITaskForKanban[] {
      return this.conversations.map(c => ({
        taskID: c.convoID,
        refNumber: c.refNumber,
        title: c.title,
        statusID: c.statusID,
        priorityLevelID: c.priorityLevelID,
        assigneeID: c.assigneeID,
        customerID: c.customerID,
        dueDate: c.dueDate,
        completedChecklistItemsCount: c.completedChecklistItemsCount,
        totalChecklistItemsCount: c.totalChecklistItemsCount,
        lastUpdatedEpochTime: c.lastUpdatedEpochTime,
        filterMatch: c.filterMatch,
        lastUpdated: c.lastUpdated,
        tags: c.tags,
        listCustomFieldValues: c.listCustomFieldValues,
      }))
    },
  },

  watch: {},

  beforeUnmount() {
    this.endPolling()
  },

  async created() {
    LocalStore.setMailboxDefaultedToKanban(this.id)

    this.isLoadingInitialData = true
    await Promise.all([
      this.fetchDetails(),
      this.fetchFilterOptionsForListing(),
      this.getStatusesForColumns(),
      this.getWorkflowStatusTransitions(),
    ])
    this.isLoadingInitialData = false
    this.initialKanbanDataLoaded = true
    LocalStore.lastItemListRouteVisited = RouteAssistant.getMailboxKanbanRoute(
      this.accountID,
      this.id,
      this.name,
    )
    this.startPolling()
    this.postCreated()
    this.applyDefaultKanbanSort()
    this.fetchDefaultFilterOptionCounts()
    await this.showInfoOnEntityLandingIfNecessary()
  },

  methods: {
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      // A load that finishes after the user left would otherwise start a poller nothing clears
      if (this.isUnmounted) {
        return
      }
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    handleSettings() {
      RouteAssistant.goToMailboxSettings(
        this.$router,
        this.$route.params.accountId,
        this.id,
      )
    },
    handleMobileSwitchToList() {
      RouteAssistant.goToMailboxListing(
        this.$router,
        this.$route.params.accountId,
        this.id,
        this.name,
      )
    },
    applyDefaultKanbanSort() {
      const savedQuery = LocalStore.getHomeSavedQuery(this.id)
      const savedSort = savedQuery ? JSON.parse(savedQuery).sort : null
      if (!this.$route.query.sort && !savedSort) {
        this.itemTraitSet.sort = SortOptionValues.STATUS
      }
    },
    async refreshKanbanBoard() {
      await Promise.all([
        this.fetchFilterOptionsForListing(),
        this.fetchResults(),
      ])
      this.fetchDefaultFilterOptionCounts()
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await MailboxesAPI.get_LastUpdatedEpochTime(
        this.id,
      )
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await Promise.all([
          this.fetchDetails(),
          this.fetchFilterOptionsForListing(),
          this.fetchResults(),
        ])
        this.fetchDefaultFilterOptionCounts()
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async fetchDetails() {
      try {
        const details = await MailboxesAPI.get_Detail(this.id)
        this.name = details.name
        this.members = details.listPeople
        this.workflow = details.workflow
        this.isDirectConnection = details.isDirectConnection
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async fetchResults() {
      const params = {
        priority_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.priorityIDs,
        ),
        status_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.statusIDs,
        ),
        status_type: this.itemTraitSet.statusType,
        assignee_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.assigneeIDs,
        ),
        assigned_not_to_you: this.itemTraitSet.assignedNotToYou,
        job_appointee_groupings_string: this.itemTraitSet
          .jobAppointeeGroupingsString,
        including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.includingTagIDs,
        ),
        not_including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.notIncludingTagIDs,
        ),
        start_due_date: this.itemTraitSet.startDueDate,
        end_due_date: this.itemTraitSet.endDueDate,
        due_type: this.itemTraitSet.dueType,
        sort: this.itemTraitSet.sort,
        search_term: this.itemTraitSet.searchTerm,
        only_watching: this.itemTraitSet.onlyWatching,
        created_within_days: this.itemTraitSet.createdWithinDays,
        last_updated_within_days: this.itemTraitSet.lastUpdatedWithinDays,
        custom_field_filters: this.itemTraitSet.customFieldFiltersString,
        customer_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.customerIDs,
        ),
        is_spam: this.itemTraitSet.isSpam,
      }

      try {
        this.totalItemCount = null
        const response = await ConversationsAPI.get_SearchForKanban(
          this.id,
          params.customer_ids,
          params.is_spam,
          params.priority_ids,
          params.status_ids,
          params.status_type,
          params.assignee_ids,
          params.assigned_not_to_you,
          params.job_appointee_groupings_string,
          params.including_tag_ids,
          params.not_including_tag_ids,
          params.due_type,
          params.start_due_date,
          params.end_due_date,
          params.search_term,
          params.only_watching,
          params.created_within_days,
          params.last_updated_within_days,
          params.custom_field_filters,
        )

        const filterMatches = response.listConversations.filter(
          c => c.filterMatch,
        )

        this.totalItemCount = filterMatches.length
        this.columnOrderings = response.listKanbanColumnOrderings
        this.customFields = response.listCustomFields || []
        this.conversations = response.listConversations
        this.lastAccessEpochTime = response.accessEpochTime

        this.customers = await CustomersAPI.post_Search(
          this.conversations.map(c => c.customerID),
        )

        const breadCrumbItems = [] as IBreadcrumbItemMenuItem[]

        filterMatches.forEach(c => {
          breadCrumbItems.push({
            itemID: c.convoID,
            entityID: this.id,
            entityName: this.name,
            entityType: EEntities.MAILBOXES,
            title: c.title,
            refNumber: c.refNumber,
          } as IBreadcrumbItemMenuItem)
        })

        BreadcrumbAssistant.cacheBreadcrumbItemMenuItems(
          breadCrumbItems,
        )
      } catch (error) {
        LocalStore.clearHomeSavedQuery(this.id)
        this.$error.emit(error)
      }
    },

    async fetchFilterOptionsForListing() {
      try {
        const response = await MailboxesAPI.get_FilterOptionsForListing(this.id)
        this.hydrateFilterOptionsForListing(response)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    // Should not await this method as it is non-essential to the function of this screen.
    async fetchDefaultFilterOptionCounts() {
      try {
        // Swallow if timeout (or other error) occurs...
        const counts = await MailboxesAPI.get_DefaultFilterOptionCounts(this.id)
        this.updateDefaultFilterOptionCounts(
          counts.listDefaultFilters,
          counts.listActiveAssigneeCounts,
        )
      } catch (error) {
        // Swallow
      }
    },

    hydrateFilterOptionsForListing(
      data: IMailboxesOutputFilterOptionsForListing,
    ) {
      this.hydrateFilterOptionsForListing_Base(data)
      this.customFilterNavItems = data.listCustomFilters.map(c => ({
        value: ItemFilterAssistant.getCustomFilterIdentifier(c.id),
        label: c.name,
        icon: 'filter',
      }))
      this.customFilterItemTraitSets = data.listCustomFilters.map(c => ({
        id: c.id,
        itemTraitSet: {
          priorityIDs: c.listPriorityIDs ? c.listPriorityIDs : [],
          statusIDs: c.listStatusIDs ? c.listStatusIDs : [],
          statusType: c.statusType ? c.statusType : '',
          assigneeIDs: c.listAssigneeIDs ? c.listAssigneeIDs : [],
          assignedNotToYou: c.assignedNotToYou,
          jobAppointeeGroupingsString: ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
            c.listJobAppointeeGroupings,
          ),
          dueType: c.dueType ? c.dueType : null,
          includingTagIDs: c.listIncludingTagIDs ? c.listIncludingTagIDs : [],
          notIncludingTagIDs: c.listNotIncludingTagIDs
            ? c.listNotIncludingTagIDs
            : [],
          startDueDate: c.startDueDate
            ? dayjs(c.startDueDate).format('YYYY-MM-DD')
            : null,
          endDueDate: c.endDueDate
            ? dayjs(c.endDueDate).format('YYYY-MM-DD')
            : null,
          searchTerm: c.searchTerm ? c.searchTerm : '',
          onlyWatching: c.onlyWatching,
          createdWithinDays: c.createdWithinDays,
          lastUpdatedWithinDays: c.lastUpdatedWithinDays,
          creatorIDs: [], // Never used, default it so the comparison is equal in isItemTraitSetEquivalent()
          customerIDs: c.listCustomerIDs ? c.listCustomerIDs : [],
          isSpam: c.isSpam,
          sort: SortOptionValues.UPDATED_RECENT,
          projectIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          mailboxIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          customFieldFiltersString: c.customFieldFiltersString
            ? c.customFieldFiltersString
            : '',
        } as IItemTraitSet,
      }))
    },

    async getMembers() {
      try {
        this.members = await MailboxesAPI.get_Members(this.id)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async createCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
          customFieldFiltersString: this.itemTraitSet.customFieldFiltersString,
        } as IMailboxesInputManageCustomFilter
        const response = await MailboxesAPI.post_CreateCustomFilter(
          this.id,
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async updateCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
          customFieldFiltersString: this.itemTraitSet.customFieldFiltersString,
        } as IMailboxesInputUpdateCustomFilter
        const response = await MailboxesAPI.put_UpdateCustomFilter(
          this.id,
          ItemFilterAssistant.getIDOfFilterIdentifier(
            this.currentFilterNavItem.value,
          ),
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async deleteCustomFilter(identifier: string) {
      try {
        const response = await CustomFiltersAPI.delete_Delete(
          ItemFilterAssistant.getIDOfFilterIdentifier(identifier),
        )

        this.fetchFilterOptionsForListing()
        if (this.currentFilterNavItem.value !== identifier) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    /* Kanban specific */
    async getStatusesForColumns() {
      try {
        this.statusesForColumns = await MailboxesAPI.get_Statuses(this.id)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async getWorkflowStatusTransitions() {
      try {
        this.workflowStatusTransitions = await MailboxesAPI.get_WorkflowStatusTransitions(
          this.id,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async handleConvoMove(
      convoID: number,
      statusID: number,
      listConvoIDsColumnOrder: Array<number>,
    ) {
      try {
        this.isHandlingMove = true
        const data = {
          lastAccessEpochTime: this.lastAccessEpochTime,
          statusID,
          listConvoIDsColumnOrder,
        } as IConvosInputUpdateForKanban
        const response = await ConversationsAPI.put_UpdateForKanban(
          this.id,
          convoID,
          data,
        )
        /* Set the new last access time */
        this.lastAccessEpochTime = response.lastUpdatedEpochTimeAfterUpdate

        if (response.needsRefresh) {
          /* Refresh the board if we were previously out of date */
          await this.refreshKanbanBoard()
        } else {
          const movedConvoToNewStatus =
            this.conversations.find(c => c.convoID === convoID).statusID !==
            statusID

          if (movedConvoToNewStatus) {
            await this.fetchResults()
          }
        }
      } catch (error) {
        this.$error.emit(error, true)
        /* Always reload the data on error */
        await this.refreshKanbanBoard()
      } finally {
        this.isHandlingMove = false
      }
    },

    sendToNewConversation() {
      RouteAssistant.goToCreateConversation(
        this.$router,
        this.accountID,
        this.id,
        this.name,
      )
    },
  },
})
</script>

<style lang="scss">
.mailbox-kanban {
  &__create-button {
    position: fixed;
    right: 15px;
    bottom: 70px;

    &.go-native-ios {
      bottom: 100px;
    }
  }

  &__mobile-options {
    .ddi-kanban {
      color: var(--color-text-third);
      font-size: 20px;
    }
  }

  &__kanban-disabled-callout {
    margin: 0 auto;
    padding-top: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 400px;
  }
}
</style>
